import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import { Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";

import AddClassService from "./Locality/Service/addClassService";
import PushNotificationService from "./Locality/Service/pushNotificationService";
import PageTitle from "../../components/PageTitle/PageTitle";
import { linkifyText } from "../../utils/linkifyText";
import { showAppConfirm } from '../../context/AlertContext';

export default function PushNotificationAdmin() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const schooleId = useMemo(() => {
    const id = userDetails?.schooleId || userDetails?.schoolId;
    return id && typeof id === "object" ? id?._id : id;
  }, [userDetails]);

  const [classesList, setClassesList] = useState([]);
  const [entireSchool, setEntireSchool] = useState(false);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [thanksAndRegards, setThanksAndRegards] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({
    percent: 0,
    message: "Preparing to send…",
  });
  const [status, setStatus] = useState(null);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editClassId, setEditClassId] = useState("");
  const [editSectionId, setEditSectionId] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editThanks, setEditThanks] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filePreviewUrls, setFilePreviewUrls] = useState([]);

  useEffect(() => {
    const urls = files.map((file) =>
      String(file.type || "").startsWith("image/") ? URL.createObjectURL(file) : null,
    );
    setFilePreviewUrls(urls);
    return () => {
      urls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [files]);

  const getClassNames = (ids, isEntireSchool) => {
    if (isEntireSchool) return "Entire School";
    const idList = Array.isArray(ids) ? ids.map((x) => String(x)) : [];
    if (idList.length === 0) return "";
    const names = classesList
      .filter((c) => idList.includes(String(c._id)))
      .map((c) => c.className)
      .filter(Boolean);
    const missingCount = Math.max(0, idList.length - names.length);
    return `${names.join(", ")}${missingCount ? ` (+${missingCount})` : ""}`;
  };

  // Flatten every class's sectionList into a single id -> name lookup so
  // section names can be resolved regardless of which class they belong to.
  const sectionNameById = useMemo(() => {
    const map = new Map();
    classesList.forEach((c) => {
      (c.sectionList || []).forEach((s) => map.set(String(s._id), s.sectionName));
    });
    return map;
  }, [classesList]);

  const getSectionNames = (ids) => {
    const idList = Array.isArray(ids) ? ids.map((x) => String(x)) : [];
    if (idList.length === 0) return "";
    return idList.map((id) => sectionNameById.get(id)).filter(Boolean).join(", ");
  };

  const sectionsForClass = useMemo(
    () => classesList.find((c) => String(c._id) === String(classId))?.sectionList || [],
    [classesList, classId],
  );

  const sectionsForEditClass = useMemo(
    () => classesList.find((c) => String(c._id) === String(editClassId))?.sectionList || [],
    [classesList, editClassId],
  );

  useEffect(() => {
    if (!schooleId) return;
    AddClassService.getAllAddClass(schooleId, "SCHOOLE")
      .then((res) => setClassesList(Array.isArray(res) ? res : res?.data || []))
      .catch(() => setClassesList([]));
  }, [schooleId]);

  const refreshAdminList = async () => {
    setLoadingList(true);
    try {
      const res = await PushNotificationService.listAdminNotifications();
      const data = res?.data || res;
      setAdminNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch {
      setAdminNotifications([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!schooleId) return;
    refreshAdminList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schooleId]);

  const canSend = Boolean(entireSchool || classId) && subject.trim() && message.trim();

  const onFilesChange = (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;
    setFiles((prev) => [...prev, ...picked]);
    // allow selecting the same file again in a second pick
    event.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const onSend = async () => {
    if (!canSend || sending) return;
    setStatus(null);
    setSending(true);
    setSendProgress({
      percent: 0,
      message: files.length
        ? "Uploading files… Please wait."
        : "Sending notification… Please wait.",
    });
    try {
      const body = new FormData();
      if (entireSchool) {
        body.append("isEntireSchool", "true");
        classesList.forEach((c) => {
          if (c?._id) body.append("classIds", String(c._id));
        });
      } else {
        body.append("classIds", classId);
        if (sectionId) body.append("sectionIds", sectionId);
      }
      body.append("subject", subject.trim());
      body.append("message", message);
      body.append("thanksAndRegards", thanksAndRegards.trim());
      files.forEach((file) => body.append("files", file));

      const res = await PushNotificationService.sendNotification(body, (progress) => {
        setSendProgress({
          percent: progress.percent,
          message:
            files.length > 0
              ? `Uploading files… ${progress.percent}%`
              : "Sending notification… Please wait.",
        });
      });

      setSendProgress({ percent: 100, message: "Finishing…" });
      const data = res?.data || res;
      const successText = `Sent successfully. Push delivered to ${data?.pushCount ?? 0} device(s).`;
      setStatus({ type: "success", text: successText });
      alert(successText);
      setSubject("");
      setMessage("");
      setThanksAndRegards("");
      setEntireSchool(false);
      setClassId("");
      setSectionId("");
      setFiles([]);
      refreshAdminList();
    } catch (e) {
      const errorText = e?.response?.data?.message || "Failed to send notification.";
      setStatus({ type: "error", text: errorText });
      alert(errorText);
    } finally {
      setSending(false);
      setSendProgress({ percent: 0, message: "Preparing to send…" });
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditClassId(String((row?.classIds || [])[0] || ""));
    setEditSectionId(String((row?.sectionIds || [])[0] || ""));
    setEditSubject(row?.subject || "");
    setEditMessage(row?.message || "");
    setEditThanks(row?.thanksAndRegards || "");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditClassId("");
    setEditSectionId("");
  };

  const canSaveEdit = Boolean(editClassId) && editSubject.trim() && editMessage.trim();

  const saveEdit = async () => {
    if (!editRow?._id || !canSaveEdit) return;
    setSavingEdit(true);
    try {
      await PushNotificationService.updateAdminNotification(editRow._id, {
        classIds: [editClassId],
        sectionIds: editSectionId ? [editSectionId] : [],
        subject: editSubject,
        message: editMessage,
        thanksAndRegards: editThanks,
      });
      closeEdit();
      refreshAdminList();
    } catch (e) {
      setStatus({
        type: "error",
        text: e?.response?.data?.message || "Failed to update notification.",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteRow = async (row) => {
    if (!row?._id) return;
    const ok = await showAppConfirm("Delete this notification?");
    if (!ok) return;
    try {
      await PushNotificationService.deleteAdminNotification(row._id);
      refreshAdminList();
    } catch (e) {
      setStatus({
        type: "error",
        text: e?.response?.data?.message || "Failed to delete notification.",
      });
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedNotifications = useMemo(() => {
    const start = page * rowsPerPage;
    return adminNotifications.slice(start, start + rowsPerPage);
  }, [adminNotifications, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(adminNotifications.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [adminNotifications.length, rowsPerPage, page]);

  return (
    <Container maxWidth="md">
      <PageTitle title=" Notification" />
      <Card style={{ padding: 16 }}>
        <Typography variant="h6" style={{ fontWeight: 700, color: "#0F3B62" }}>
          Send to Parents
        </Typography>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={entireSchool}
                disabled={sending || Boolean(classId) || Boolean(sectionId)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEntireSchool(checked);
                  if (checked) {
                    setClassId("");
                    setSectionId("");
                  }
                }}
              />
            }
            label={
              classId || sectionId
                ? "Entire School (clear Class/Section first)"
                : "Entire School (all students / parents)"
            }
          />
        </Box>

        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth disabled={entireSchool || sending}>
                <InputLabel id="class-select-label">
                  {entireSchool ? "Class (disabled — Entire School)" : "Class"}
                </InputLabel>
                <Select
                  labelId="class-select-label"
                  value={classId}
                  disabled={entireSchool || sending}
                  onChange={(e) => {
                    const next = e.target.value;
                    setEntireSchool(false);
                    setClassId(next);
                    setSectionId("");
                  }}
                  label={entireSchool ? "Class (disabled — Entire School)" : "Class"}
                >
                  <MenuItem value="">
                    <em>Select class</em>
                  </MenuItem>
                  {classesList.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth disabled={entireSchool || !classId || sending}>
                <InputLabel id="section-select-label" shrink>
                  {entireSchool ? "Section (disabled — Entire School)" : "Section (optional)"}
                </InputLabel>
                <Select
                  labelId="section-select-label"
                  value={sectionId}
                  onChange={(e) => {
                    setEntireSchool(false);
                    setSectionId(e.target.value);
                  }}
                  label={entireSchool ? "Section (disabled — Entire School)" : "Section (optional)"}
                  displayEmpty
                  disabled={entireSchool || !classId || sending}
                >
                  <MenuItem value="">
                    <em>All Sections</em>
                  </MenuItem>
                  {sectionsForClass.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box mt={2}>
          <TextField
            label="Subject"
            variant="outlined"
            fullWidth
            value={subject}
            disabled={sending}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Box>

        <Box mt={2}>
          <TextField
            label="Message"
            variant="outlined"
            fullWidth
            multiline
            minRows={6}
            value={message}
            disabled={sending}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message. Include https://… links — parents can click them."
            helperText="Tip: use full URLs starting with http:// or https://"
          />
          {message.trim() && /https?:\/\//i.test(message) && (
            <Box
              mt={1.5}
              p={1.5}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                background: "#f8fafc",
              }}
            >
              <Typography variant="caption" style={{ color: "#64748b", fontWeight: 700, display: "block", marginBottom: 6 }}>
                Link preview (how parents will see it)
              </Typography>
              <Typography
                variant="body2"
                style={{ color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.7 }}
              >
                {linkifyText(message)}
              </Typography>
            </Box>
          )}
        </Box>

        <Box mt={2}>
          <TextField
            label="Thanks And Regards (optional)"
            variant="outlined"
            fullWidth
            value={thanksAndRegards}
            disabled={sending}
            onChange={(e) => setThanksAndRegards(e.target.value)}
          />
        </Box>

        <Box mt={2}>
          <input
            accept="image/*,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            id="push-notification-files"
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={onFilesChange}
          />
          <label htmlFor="push-notification-files">
            <Button
              variant="outlined"
              component="span"
              disabled={sending}
              style={{ borderColor: "#0F3B62", color: "#0F3B62" }}
            >
              {files.length > 0 ? "Add More Files" : "Upload Images / Excel"}
            </Button>
          </label>
          {files.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" style={{ color: "#475569", fontWeight: 700, marginBottom: 8 }}>
                Selected Files ({files.length})
              </Typography>
              <Grid container spacing={2}>
                {files.map((file, index) => (
                  <Grid item xs={6} sm={4} md={3} key={`${file.name}-${index}`}>
                    <Box
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        padding: 8,
                        background: "#f8fafc",
                        position: "relative",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => removeFile(index)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(255,255,255,0.9)",
                          zIndex: 1,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      {filePreviewUrls[index] ? (
                        <img
                          src={filePreviewUrls[index]}
                          alt={file.name}
                          style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8 }}
                        />
                      ) : (
                        <Box
                          style={{
                            height: 120,
                            borderRadius: 8,
                            background: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px dashed #cbd5e1",
                            color: "#0F3B62",
                            fontWeight: 800,
                          }}
                        >
                          FILE
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        style={{ display: "block", marginTop: 6, color: "#64748b", wordBreak: "break-word" }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>

        {status && (
          <Box mt={2}>
            <Typography
              variant="body2"
              style={{ color: status.type === "success" ? "#15803d" : "#b91c1c" }}
            >
              {status.text}
            </Typography>
          </Box>
        )}

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            disabled={!canSend || sending}
            onClick={onSend}
            style={{ backgroundColor: "#0F3B62", color: "#ffffff" }}
          >
            {sending ? (
              <>
                <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                Sending…
              </>
            ) : (
              "Send Notification"
            )}
          </Button>
        </Box>
      </Card>

      <Box mt={3}>
        <Card style={{ padding: 16 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
            <Typography variant="h6" style={{ fontWeight: 800, color: "#0F3B62" }}>
              Sent Notifications
            </Typography>
            <Button variant="outlined" onClick={refreshAdminList}>
              Refresh
            </Button>
          </Box>

          <Box mt={2} style={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Subject</b></TableCell>
                  <TableCell><b>Classes</b></TableCell>
                  <TableCell><b>Sections</b></TableCell>
                  <TableCell><b>Attachments</b></TableCell>
                  <TableCell><b>Created</b></TableCell>
                  <TableCell align="right"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingList ? (
                  <TableRow>
                    <TableCell colSpan={6}>Loading...</TableCell>
                  </TableRow>
                ) : adminNotifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>No notifications found.</TableCell>
                  </TableRow>
                ) : (
                  pagedNotifications.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.subject}
                      </TableCell>
                      <TableCell
                        style={{
                          maxWidth: 320,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={getClassNames(row.classIds, row.isEntireSchool)}
                      >
                        {getClassNames(row.classIds, row.isEntireSchool) || "-"}
                      </TableCell>
                      <TableCell
                        style={{
                          maxWidth: 220,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={row.isEntireSchool ? "All Sections" : getSectionNames(row.sectionIds)}
                      >
                        {row.isEntireSchool ? "All" : (getSectionNames(row.sectionIds) || "All")}
                      </TableCell>
                      <TableCell>{Array.isArray(row.attachments) ? row.attachments.length : (Array.isArray(row.imageUrls) ? row.imageUrls.length : 0)}</TableCell>
                      <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : ""}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(row)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteRow(row)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {!loadingList && adminNotifications.length > 0 && (
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={adminNotifications.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Box>
        </Card>
      </Box>

      <Dialog
        open={sending}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: 700, color: "#0F3B62" }}>
          Sending Notification
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={2}
            px={1}
          >
            <CircularProgress size={48} style={{ color: "#0F3B62", marginBottom: 20 }} />
            <Typography variant="body1" align="center" style={{ fontWeight: 600 }}>
              {sendProgress.message}
            </Typography>
            <Box width="100%" mt={2.5}>
              <LinearProgress
                variant={
                  files.length > 0 && sendProgress.percent > 0
                    ? "determinate"
                    : "indeterminate"
                }
                value={sendProgress.percent}
                style={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              style={{ marginTop: 16 }}
            >
              Please wait until all files are uploaded and the notification is sent.
              Do not close this window.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="edit-class-select-label">Class</InputLabel>
                  <Select
                    labelId="edit-class-select-label"
                    value={editClassId}
                    onChange={(e) => {
                      setEditClassId(e.target.value);
                      setEditSectionId("");
                    }}
                    label="Class"
                  >
                    {classesList.map((c) => (
                      <MenuItem key={c._id} value={String(c._id)}>
                        {c.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth disabled={!editClassId}>
                  <InputLabel id="edit-section-select-label" shrink>
                    Section (optional)
                  </InputLabel>
                  <Select
                    labelId="edit-section-select-label"
                    value={editSectionId}
                    onChange={(e) => setEditSectionId(e.target.value)}
                    label="Section (optional)"
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>All Sections</em>
                    </MenuItem>
                    {sectionsForEditClass.map((s) => (
                      <MenuItem key={s._id} value={String(s._id)}>
                        {s.sectionName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box mt={2}>
            <TextField label="Subject" variant="outlined" fullWidth value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
          </Box>
          <Box mt={2}>
            <TextField label="Message" variant="outlined" fullWidth multiline minRows={5} value={editMessage} onChange={(e) => setEditMessage(e.target.value)} />
          </Box>
          <Box mt={2}>
            <TextField label="Thanks And Regards (optional)" variant="outlined" fullWidth value={editThanks} onChange={(e) => setEditThanks(e.target.value)} />
          </Box>
          <Box mt={1}>
            <Typography variant="caption" style={{ color: "#64748b" }}>
              Note: attachments cannot be edited here (delete and re-send to change files).
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={savingEdit || !canSaveEdit} onClick={saveEdit} style={{ backgroundColor: "#0F3B62" }}>
            {savingEdit ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

